import { type ElasticsearchQuery, type FacetFieldConfig } from '@searchkit/api';

import {
  allWNBAQuery,
  eplOnlySoccerQuery,
  lineReductionQuery,
  noWorldCup26Query,
  supportedCategoryGroupsQuery,
  validMatchupCentreQuery,
} from '~/search-domain/queries';
import { type SearchDomainContext, type SearchRuntimeSortingConfig, type SearchUiContext } from '~/search-domain/types';

const resultAttributes = [
  'betName',
  'betPercentage',
  'categoryName',
  'categoryGroupId',
  'categoryGroupName',
  'categoryId',
  'eventDisplayName',
  'eventId',
  'marketDisplayName',
  'subeventId',
  'subeventName',
  'subeventStartDate',
  'subeventStartTime',
  'teamData',
  'expectedValue',
  'expectedValueStatus',
];

export function getMatchupCentreBaseFilters(context: SearchDomainContext) {
  const baseFilters: ElasticsearchQuery[] = [validMatchupCentreQuery];

  if (context.searchFlags.soccerEplOnly) {
    baseFilters.push(eplOnlySoccerQuery);
  }

  if (context.searchFlags.allWnba) {
    baseFilters.push(allWNBAQuery);
  }

  if (!context.searchFlags.newCategoryGroups) {
    baseFilters.push(supportedCategoryGroupsQuery);
  }

  if (context.searchFlags.lineReduction) {
    baseFilters.push(lineReductionQuery);
  }

  if (!context.searchFlags.worldCup26) {
    baseFilters.push(noWorldCup26Query);
  }

  return baseFilters;
}

export function getMatchupCentreFacetAttributes(context: SearchDomainContext): FacetFieldConfig[] {
  const repubCountry = context.repubCountry;
  if (!repubCountry) return [];
  const areInternalSearchToolsEnabled = context.searchFlags.internalSearchTools;

  const internalSearchToolFacets: FacetFieldConfig[] = areInternalSearchToolsEnabled
    ? [{ attribute: 'bookmakerCode', field: 'bookmakerCode', type: 'string' }]
    : [];

  return [
    ...internalSearchToolFacets,
    { attribute: 'marketDisplayName', field: `marketDisplayNameMap.${repubCountry}.keyword`, type: 'string' },
    { attribute: 'eventDisplayName', field: 'eventDisplayName', type: 'string' },
    { attribute: 'eventId', field: 'eventId', type: 'numeric' },
    { attribute: 'subeventId', field: 'subeventId', type: 'numeric' },
    { attribute: 'subeventStartDate', field: 'subeventStartDate', type: 'string' },
    { attribute: 'subeventStartTime', field: 'subeventStartTime', type: 'string' },
    { attribute: 'expectedValue', field: 'expectedValue', type: 'numeric' },
    { attribute: 'expectedValueStatus', field: 'expectedValueStatus', type: 'numeric' },
    { attribute: 'teamData.away.name', field: 'teamData.away.name.keyword', type: 'string' },
    { attribute: 'teamData.away.nickName', field: 'teamData.away.nickName.keyword', type: 'string' },
    { attribute: 'teamData.away.shortName', field: 'teamData.away.shortName.keyword', type: 'string' },
    { attribute: 'teamData.home.name', field: 'teamData.home.name.keyword', type: 'string' },
    { attribute: 'teamData.home.nickName', field: 'teamData.home.nickName.keyword', type: 'string' },
    { attribute: 'teamData.home.shortName', field: 'teamData.home.shortName.keyword', type: 'string' },
  ];
}

export function getMatchupCentreSearchSettings() {
  const searchAttributes = [
    'teamData.away.name',
    'teamData.away.nickName',
    'teamData.away.shortName',
    'teamData.home.name',
    'teamData.home.nickName',
    'teamData.home.shortName',
  ];

  return {
    highlights: searchAttributes,
    results: resultAttributes,
    search: searchAttributes,
  };
}

export function getMatchupCentreSorting(_context: SearchUiContext): SearchRuntimeSortingConfig {
  return {
    options: [{ field: 'subeventStartTime', label: 'Event Time', order: 'asc' }],
  };
}
